import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  LinearProgress,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useNavigate } from 'react-router-dom';
import { useMatching } from '../hooks/useMatching';
import { CatalogoService } from '../services/catalogoService';
import { SugerenciaMatching } from '../types';
import { CrearCanonicoModal } from '../components';

export function MatchingPage() {
  const navigate = useNavigate();
  const {
    pendientes,
    loadingPendientes,
    loadingConfirmar,
    error,
    successMessage,
    cargarPendientes,
    confirmarMatch,
    crearCanonicoYMatch,
    limpiarMensajes,
  } = useMatching();

  const [productoActualId, setProductoActualId] = useState<number | null>(null);
  const [sugerencias, setSugerencias] = useState<SugerenciaMatching[]>([]);
  const [loadingSugerencias, setLoadingSugerencias] = useState(false);
  const [modalCrearOpen, setModalCrearOpen] = useState(false);
  const [nombreSugeridoModal, setNombreSugeridoModal] = useState('');

  useEffect(() => {
    cargarPendientes();
  }, [cargarPendientes]);

  const abrirProducto = async (proveedorProductoId: number, nombreOriginal: string) => {
    setProductoActualId(proveedorProductoId);
    setNombreSugeridoModal(nombreOriginal);
    setLoadingSugerencias(true);
    try {
      const data = await CatalogoService.obtenerSugerencias(proveedorProductoId);
      setSugerencias(data);
    } catch {
      setSugerencias([]);
    } finally {
      setLoadingSugerencias(false);
    }
  };

  const handleConfirmarMatch = async (canonicoId: number) => {
    if (!productoActualId) return;
    await confirmarMatch(productoActualId, canonicoId);
    setSugerencias([]);
    setProductoActualId(null);
    cargarPendientes();
  };

  const handleCrearCanonico = async (nombre: string, tags: string[], subcategoriaId?: number) => {
    if (!productoActualId) return;
    await crearCanonicoYMatch(productoActualId, nombre, tags, subcategoriaId);
    setModalCrearOpen(false);
    setSugerencias([]);
    setProductoActualId(null);
    cargarPendientes();
  };

  const totalPendientes = pendientes.reduce((acc, p) => acc + p.pendientes, 0);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/catalogo')}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" fontWeight="bold" color="#3d1e3d">
            Cola de Matching
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Asocia productos de proveedores a sus canónicos correspondientes
          </Typography>
        </Box>
      </Box>

      {loadingPendientes ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 3 }}>
          {/* Panel izquierdo: lista de proveedores con pendientes */}
          <Box>
            <Card sx={{ backgroundColor: '#f7edf5' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" color="#3d1e3d" gutterBottom>
                  Pendientes por proveedor
                </Typography>
                <Chip
                  label={`${totalPendientes} total`}
                  size="small"
                  sx={{ backgroundColor: '#e8d8e8', color: '#3d1e3d', mb: 2 }}
                />
                {pendientes.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CheckCircleIcon sx={{ fontSize: 48, color: '#3d7a6e' }} />
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      ¡Sin pendientes!
                    </Typography>
                  </Box>
                ) : (
                  pendientes.map((p) => (
                    <Box
                      key={p.proveedorId}
                      data-testid="matching-proveedor-item"
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1,
                        borderBottom: '1px solid #e8d8e8',
                      }}
                    >
                      <Typography variant="body2" color="#3d1e3d" data-testid="matching-proveedor-nombre">
                        {p.proveedor}
                      </Typography>
                      <Chip
                        label={p.pendientes}
                        size="small"
                        data-testid="matching-proveedor-conteo"
                        sx={{ backgroundColor: '#3d1e3d', color: '#fff', minWidth: 36 }}
                      />
                    </Box>
                  ))
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Panel derecho: producto actual + sugerencias */}
          <Box>
            {productoActualId === null ? (
              <Card sx={{ backgroundColor: '#fafafa', border: '2px dashed #e8d8e8' }}>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="h6" color="text.secondary">
                    Selecciona un producto para resolver
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    (función de exploración por proveedor en próxima versión)
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Card sx={{ backgroundColor: '#f7edf5' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" color="#3d1e3d" gutterBottom>
                    Sugerencias de matching
                  </Typography>

                  {loadingSugerencias ? (
                    <LinearProgress sx={{ my: 2 }} />
                  ) : sugerencias.length === 0 ? (
                    <Box>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        Sin sugerencias automáticas.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddCircleIcon />}
                        onClick={() => setModalCrearOpen(true)}
                        sx={{ backgroundColor: '#3d1e3d', '&:hover': { backgroundColor: '#5a2e5a' } }}
                      >
                        Crear nuevo canónico
                      </Button>
                    </Box>
                  ) : (
                    <>
                      {sugerencias.map((s) => (
                        <Box key={s.canonicoId} sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="body1" fontWeight="bold" color="#3d1e3d">
                                {s.nombre}
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {s.tags.slice(0, 4).map((tag) => (
                                  <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                ))}
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(100, s.score * 1000)}
                                  sx={{
                                    flexGrow: 1,
                                    height: 6,
                                    borderRadius: 3,
                                    backgroundColor: '#e8d8e8',
                                    '& .MuiLinearProgress-bar': { backgroundColor: '#3d1e3d' },
                                  }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {Math.round(s.score * 1000) / 10}%
                                </Typography>
                              </Box>
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              disabled={loadingConfirmar}
                              onClick={() => handleConfirmarMatch(s.canonicoId)}
                              sx={{ ml: 2, backgroundColor: '#3d7a6e', '&:hover': { backgroundColor: '#2d5a50' } }}
                            >
                              {loadingConfirmar ? <CircularProgress size={16} /> : 'Confirmar'}
                            </Button>
                          </Box>
                          <Divider />
                        </Box>
                      ))}

                      <Button
                        variant="outlined"
                        startIcon={<AddCircleIcon />}
                        onClick={() => setModalCrearOpen(true)}
                        sx={{ mt: 1, borderColor: '#3d1e3d', color: '#3d1e3d' }}
                      >
                        Crear nuevo canónico
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>
      )}

      {/* Modal crear canónico */}
      <CrearCanonicoModal
        open={modalCrearOpen}
        nombreSugerido={nombreSugeridoModal}
        onClose={() => setModalCrearOpen(false)}
        onConfirmar={handleCrearCanonico}
        loading={loadingConfirmar}
      />

      {/* Snackbar éxito */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={limpiarMensajes}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={limpiarMensajes}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Snackbar error */}
      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={limpiarMensajes}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={limpiarMensajes}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
}
